import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { ApiService } from './api.service';

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role: string;
}

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<AdminUser | null>(this.getStoredUser());
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private api: ApiService,
    private router: Router
  ) {}

  login(email: string, password: string): Observable<{ token: string; user: AdminUser }> {
    return this.api.post<{ accessToken: string; refreshToken: string; expiresAt: string; user: { id: string; email: string; firstName: string; lastName: string; displayName?: string; roles: string[] } }>('/auth/admin/login', { email, password }).pipe(
      tap((data) => {
        const token = data?.accessToken;
        const u = data?.user;
        if (!token || !u) throw new Error('Invalid login response');
        const user: AdminUser = {
          id: u.id,
          email: u.email,
          name: u.displayName ?? ([u.firstName, u.lastName].filter(Boolean).join(' ') || u.email),
          role: u.roles?.[0] ?? 'Admin'
        };
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        this.currentUserSubject.next(user);
      }),
      map((data) => {
        const u = data!.user;
        return {
          token: data!.accessToken,
          user: {
            id: u.id,
            email: u.email,
            name: u.displayName ?? ([u.firstName, u.lastName].filter(Boolean).join(' ') || u.email),
            role: u.roles?.[0] ?? 'Admin'
          } as AdminUser
        };
      })
    );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  get currentUser(): AdminUser | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  private getStoredUser(): AdminUser | null {
    const stored = localStorage.getItem(USER_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as AdminUser;
    } catch {
      return null;
    }
  }
}
